import { useMemo } from 'react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { usePreisToolPriceWritebackStatus } from '@/api/preis-tool/preis-tool.hooks';
import type { PreisToolPriceHistoryChange } from '@/api/preis-tool/preis-tool.types';
import { DsTable } from '@/modules/internal-v2/shared/ds-table.component';
import { PRICE_CHANGE_STATUS, channelLabel, fmtDateTime, fmtEUR, writebackHint } from '../helpers';

interface PriceChangeLogProps {
  changes: PreisToolPriceHistoryChange[];
  isPending: boolean;
  isError: boolean;
  /** Shown when there are no changes. */
  emptyMessage: string;
}

const col = createColumnHelper<PreisToolPriceHistoryChange>();

const mono = { fontFamily: 'var(--font-mono)' } as const;

function note(entry: PreisToolPriceHistoryChange) {
  if (entry.status === 'FAILED') {
    return `${entry.errorMessage ?? 'Unbekannter Fehler'} · ${entry.attempts} Versuch${entry.attempts === 1 ? '' : 'e'}`;
  }
  if (entry.status === 'TRANSFERRED' && entry.transferredAt) {
    return `in SAP seit ${fmtDateTime(entry.transferredAt)}`;
  }
  if (entry.status === 'SUPERSEDED') {
    return entry.errorMessage
      ? `nach Fehler durch eine neuere Änderung ersetzt · ${entry.errorMessage}`
      : 'durch eine neuere Änderung ersetzt';
  }
  if (entry.status === 'PENDING' && entry.errorMessage) {
    return `Versuch ${entry.attempts} fehlgeschlagen, wird wiederholt · ${entry.errorMessage}`;
  }
  return '';
}

/** Sale-price changes of one item with the transfer status of each change. */
export function PriceChangeLog({ changes: rows, isPending, isError, emptyMessage }: PriceChangeLogProps) {
  const { data: writeback } = usePreisToolPriceWritebackStatus();
  const hint = writebackHint(writeback?.mode);

  const columns = useMemo(
    () => [
      col.accessor('createdAt', {
        header: 'Zeitpunkt',
        size: 150,
        enableSorting: false,
        cell: (info) => <span style={mono}>{fmtDateTime(info.getValue())}</span>,
      }),
      col.accessor('field', {
        header: 'Kanal',
        size: 90,
        enableSorting: false,
        cell: (info) => {
          const field = info.getValue();
          return field ? channelLabel(field) : `Preisliste ${info.row.original.priceListId}`;
        },
      }),
      col.accessor('oldPrice', {
        header: 'Vorher',
        size: 90,
        enableSorting: false,
        meta: { numeric: true },
        cell: (info) => <span style={{ ...mono, color: 'var(--ink-3)' }}>{fmtEUR(info.getValue())}</span>,
      }),
      col.accessor('newPrice', {
        header: 'Neu',
        size: 90,
        enableSorting: false,
        meta: { numeric: true },
        cell: (info) => <span style={{ ...mono, fontWeight: 600 }}>{fmtEUR(info.getValue())}</span>,
      }),
      col.accessor('status', {
        header: 'SAP',
        size: 120,
        enableSorting: false,
        cell: (info) => {
          const status = PRICE_CHANGE_STATUS[info.getValue()];
          return <span className={`ds-pill ${status.cls}`}>{status.label}</span>;
        },
      }),
      col.display({
        id: 'note',
        header: 'Hinweis',
        meta: { flex: true },
        cell: ({ row }) => {
          const text = note(row.original);
          return (
            <span
              style={{
                color: row.original.status === 'FAILED' ? 'var(--neg)' : 'var(--ink-3)',
                fontSize: 'var(--fs-xs)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={text || undefined}
            >
              {text || '—'}
            </span>
          );
        },
      }),
      col.accessor('changedByEmail', {
        header: 'Geändert von',
        size: 220,
        enableSorting: false,
        cell: (info) => (
          <span style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-xs)' }}>{info.getValue() ?? '—'}</span>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (r) => r.id,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="pt-history-changes">
      {hint && (
        <div className="pt-supplier-offers-header">
          <div className="hint">{hint}</div>
        </div>
      )}
      <DsTable
        table={table}
        fixedLayout
        emptyMessage={
          isPending
            ? 'Lade…'
            : isError
              ? 'Fehler beim Laden der Preisänderungen.'
              : emptyMessage
        }
      />
    </div>
  );
}
